/**
 * SetupWizard.jsx — First-time setup wizard shown to ADMIN on fresh install.
 *   Step 1 — Academy identity   (name, tagline, logo)
 *   Step 2 — Contact info       (public support phone + email)
 *   Step 3 — Region & payments  (country/currency preset)
 *   Step 4 — Communications     (optional SMTP + WhatsApp)
 *   Step 5 — Done
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { markSetupComplete } from '../components/RequireAuth';
import { getSetupStatus } from '../api/setup';
import API from '../api';
import { uploadBrandingLogo } from '../api/branding';
import { getIntegrationStatus, saveEmailSettings, saveWhatsAppIntegration } from '../api/integrations';
import { useBranding } from '../context/BrandingContext';
import { useRegionalSettings } from '../context/RegionalSettingsContext';
import {
    ONBOARDING_REGION_KEYS,
    REGION_PRESETS,
    regionalPresetPayload,
    getRegionalPresetLabel,
} from '../config/regionalPresets';
import { saveOnboardingRegionalSettings } from '../api/regionalSettings';
import appConfig from '../config/appConfig';
import { apiErrorMessage } from '../utils/apiError';
import { UI_LANGUAGES, serializeEnabledUiLanguages } from '../utils/uiLanguage';

// ─── Tiny shared primitives ───────────────────────────────────────────────────
function Label({ children }) {
    return <label className="block text-sm font-medium text-slate-700 mb-1.5">{children}</label>;
}
function TextInput({ value, onChange, placeholder, maxLength, type = 'text' }) {
    return (
        <input
            type={type}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
        />
    );
}

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepDots({ current, total }) {
    return (
        <div className="flex items-center gap-2">
            {Array.from({ length: total }).map((_, i) => (
                <div
                    key={i}
                    className={`h-2 rounded-full transition-all duration-300 ${i === current
                        ? 'w-6 bg-primary-600'
                        : i < current
                            ? 'w-2 bg-primary-300'
                            : 'w-2 bg-slate-200'
                        }`}
                />
            ))}
        </div>
    );
}

// ─── Step 1: Academy Identity ─────────────────────────────────────────────────
function StepAcademy({ data, onChange, onNext }) {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const [logoPreview, setLogoPreview] = useState(data.logoPreview || null);
    const [error, setError] = useState('');

    function handleLogoChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) { setError(t('setupWizard.step1.logoTooLarge')); return; }
        setError('');
        const reader = new FileReader();
        reader.onload = ev => {
            setLogoPreview(ev.target.result);
            onChange({ ...data, logoFile: file, logoPreview: ev.target.result });
        };
        reader.readAsDataURL(file);
    }

    function handleNext() {
        if (!data.appName?.trim()) { setError(t('setupWizard.step1.nameRequired')); return; }
        setError('');
        onNext();
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-bold text-slate-900">{t('setupWizard.step1.title')}</h2>
                <p className="mt-1 text-sm text-slate-500">{t('setupWizard.step1.description')}</p>
            </div>

            <div>
                <Label>{t('setupWizard.step1.logo')} <span className="text-slate-400 font-normal">{t('setupWizard.optional')}</span></Label>
                <div
                    onClick={() => fileInputRef.current?.click()}
                    className="group relative flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center transition hover:border-primary-300 hover:bg-primary-50"
                >
                    {logoPreview ? (
                        <img src={logoPreview} alt={t('setupWizard.step1.logoPreviewAlt')} className="h-20 w-auto object-contain" />
                    ) : (
                        <>
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-sm border border-slate-200 text-slate-400">
                                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                                </svg>
                            </div>
                            <p className="mt-2 text-sm font-medium text-slate-600">{t('setupWizard.step1.clickUpload')}</p>
                            <p className="text-xs text-slate-400">{t('setupWizard.step1.logoFormats')}</p>
                        </>
                    )}
                    {logoPreview && <p className="mt-2 text-xs text-primary-600 font-medium">{t('setupWizard.step1.clickChange')}</p>}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
            </div>

            <div>
                <Label>{t('setupWizard.step1.academyName')} <span className="text-red-400">*</span></Label>
                <TextInput value={data.appName} onChange={v => onChange({ ...data, appName: v })} placeholder={t('setupWizard.step1.namePlaceholder')} maxLength={80} />
            </div>

            <div>
                <Label>{t('setupWizard.step1.tagline')} <span className="text-slate-400 font-normal">{t('setupWizard.optional')}</span></Label>
                <TextInput value={data.tagline} onChange={v => onChange({ ...data, tagline: v })} placeholder={t('setupWizard.step1.taglinePlaceholder')} maxLength={120} />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button type="button" onClick={handleNext}
                className="w-full rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300">
                {t('setupWizard.continue')}
            </button>
        </div>
    );
}

// ─── Step 2: Contact Info ─────────────────────────────────────────────────────
function StepContact({ data, onChange, onBack, onNext, saving, error }) {
    const { t } = useTranslation();
    const [localError, setLocalError] = useState('');

    function handleNext() {
        if (!data.supportPhone?.trim()) { setLocalError(t('setupWizard.step2.phoneRequired')); return; }
        if (!data.supportEmail?.trim()) { setLocalError(t('setupWizard.step2.emailRequired')); return; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.supportEmail.trim())) {
            setLocalError(t('setupWizard.step2.emailInvalid'));
            return;
        }
        setLocalError('');
        onNext();
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-bold text-slate-900">{t('setupWizard.step2.title')}</h2>
                <p className="mt-1 text-sm text-slate-500">{t('setupWizard.step2.description')}</p>
            </div>

            <div className="space-y-4">
                <div>
                    <Label>{t('setupWizard.step2.supportPhone')} <span className="text-red-400">*</span></Label>
                    <TextInput value={data.supportPhone} onChange={v => onChange({ ...data, supportPhone: v })}
                        placeholder={t('setupWizard.step2.phonePlaceholder')} maxLength={30} type="tel" />
                    <p className="mt-1 text-xs text-slate-400">{t('setupWizard.step2.phoneHint')}</p>
                </div>

                <div>
                    <Label>{t('setupWizard.step2.supportEmail')} <span className="text-red-400">*</span></Label>
                    <TextInput value={data.supportEmail} onChange={v => onChange({ ...data, supportEmail: v })}
                        placeholder={t('setupWizard.step2.emailPlaceholder')} maxLength={150} type="email" />
                    <p className="mt-1 text-xs text-slate-400">{t('setupWizard.step2.emailHint')}</p>
                </div>
            </div>

            {(localError || error) && <p className="text-sm text-red-500">{localError || error}</p>}

            <div className="flex gap-3">
                <button type="button" onClick={onBack}
                    className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                    {t('setupWizard.back')}
                </button>
                <button type="button" onClick={handleNext} disabled={saving}
                    className="flex-[2] rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-60 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300">
                    {saving ? t('setupWizard.saving') : t('setupWizard.continue')}
                </button>
            </div>
        </div>
    );
}

// ─── Step 3: Region & payments ────────────────────────────────────────────────
function StepRegional({
    preset,
    onPresetChange,
    enabledUiLanguages,
    defaultUiLanguage,
    onEnabledUiLanguagesChange,
    onDefaultUiLanguageChange,
    onBack,
    onNext,
    saving,
    error,
}) {
    const { t } = useTranslation();
    const enabled = Array.isArray(enabledUiLanguages) && enabledUiLanguages.length
        ? enabledUiLanguages
        : ['en'];

    function toggleLang(code, on) {
        if (code === 'en') return;
        let next = on ? [...enabled, code] : enabled.filter((c) => c !== code);
        if (!next.includes('en')) next = ['en', ...next];
        // unique
        next = UI_LANGUAGES.filter((c) => next.includes(c));
        onEnabledUiLanguagesChange(next);
        if (!next.includes(defaultUiLanguage)) {
            onDefaultUiLanguageChange(next[0] || 'en');
        }
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-bold text-slate-900">{t('setupWizard.step3.title')}</h2>
                <p className="mt-1 text-sm text-slate-500">{t('setupWizard.step3.description')}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
                {ONBOARDING_REGION_KEYS.map((key) => {
                    const p = REGION_PRESETS[key];
                    return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onPresetChange(key)}
                        className={`rounded-xl border px-4 py-3 text-left text-sm transition ${
                            preset === key
                                ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-100'
                                : 'border-slate-200 bg-white hover:border-primary-200'
                        }`}
                    >
                        <p className="font-semibold text-slate-900">{getRegionalPresetLabel(key)}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{p.timezone}</p>
                    </button>
                    );
                })}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-4 space-y-3">
                <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                        {t('setupWizard.step3.uiLanguagesTitle', { defaultValue: 'App languages' })}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {t('setupWizard.step3.uiLanguagesHint', {
                            defaultValue: 'English is always on. Add more only if your academy needs them. With a single language, the Header language menu stays hidden.',
                        })}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {UI_LANGUAGES.map((code) => {
                        const on = enabled.includes(code);
                        return (
                            <label
                                key={code}
                                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                                    on ? 'border-primary-300 bg-white' : 'border-slate-200 bg-white/60'
                                } ${code === 'en' ? 'opacity-90' : 'cursor-pointer'}`}
                            >
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-slate-300 text-primary-600"
                                    checked={on}
                                    disabled={code === 'en'}
                                    onChange={(e) => toggleLang(code, e.target.checked)}
                                />
                                <span className="font-medium text-slate-800">
                                    {t(`pages.profile.lang_${code}`)}
                                </span>
                            </label>
                        );
                    })}
                </div>
                {enabled.length > 1 && (
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                            {t('setupWizard.step3.defaultUiLanguage', { defaultValue: 'Default language' })}
                        </label>
                        <select
                            className="w-full max-w-xs rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                            value={enabled.includes(defaultUiLanguage) ? defaultUiLanguage : enabled[0]}
                            onChange={(e) => onDefaultUiLanguageChange(e.target.value)}
                        >
                            {enabled.map((code) => (
                                <option key={code} value={code}>{t(`pages.profile.lang_${code}`)}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex gap-3">
                <button type="button" onClick={onBack}
                    className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                    {t('setupWizard.back')}
                </button>
                <button type="button" onClick={onNext} disabled={saving}
                    className="flex-[2] rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-60 transition-colors">
                    {saving ? t('setupWizard.saving') : t('setupWizard.continue')}
                </button>
            </div>
        </div>
    );
}

// ─── Step 4: Communications (optional) ───────────────────────────────────────
function StepCommunications({
    supportEmail,
    emailData,
    onEmailChange,
    emailPassword,
    onEmailPasswordChange,
    whatsappData,
    onWhatsappChange,
    whatsappToken,
    onWhatsappTokenChange,
    onBack,
    onSaveEmail,
    onSkipEmail,
    onSaveWhatsApp,
    onSkipWhatsApp,
    onFinish,
    saving,
    error,
    emailSaved,
    whatsappSaved,
    emailSkipped,
    whatsappSkipped,
}) {
    const { t } = useTranslation();
    const waLicensed = whatsappData?.licenseWhatsAppEnabled;
    const emailPasswordConfigured = emailData?.smtpPassword === 'CONFIGURED';
    const canSaveEmail = emailData?.smtpHost && emailData?.fromEmail && (emailPasswordConfigured || emailPassword.trim());

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-bold text-slate-900">{t('setupWizard.step4.title')}</h2>
                <p className="mt-1 text-sm text-slate-500">{t('setupWizard.step4.description')}</p>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {t('setupWizard.step4.skipHint')}
            </div>

            <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h3 className="font-semibold text-slate-900">{t('setupWizard.step4.emailSection')}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{t('setupWizard.step4.emailSectionHint')}</p>
                    </div>
                    {emailSaved && <span className="text-xs font-semibold text-emerald-700">{t('setupWizard.saved')}</span>}
                    {emailSkipped && <span className="text-xs font-semibold text-slate-500">{t('setupWizard.skipped')}</span>}
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                        <Label>{t('setupWizard.step4.smtpHost')}</Label>
                        <TextInput value={emailData.smtpHost || ''} onChange={(v) => onEmailChange({ ...emailData, smtpHost: v })} placeholder="smtp.yourprovider.com" />
                    </div>
                    <div>
                        <Label>{t('setupWizard.step4.smtpPort')}</Label>
                        <TextInput type="number" value={String(emailData.smtpPort || 587)} onChange={(v) => onEmailChange({ ...emailData, smtpPort: Number(v) || 587 })} placeholder="587" />
                    </div>
                    <div>
                        <Label>{t('setupWizard.step4.smtpUsername')}</Label>
                        <TextInput value={emailData.smtpUsername || ''} onChange={(v) => onEmailChange({ ...emailData, smtpUsername: v })} />
                    </div>
                    <div>
                        <Label>{t('setupWizard.step4.smtpPassword')}</Label>
                        <TextInput
                            type="password"
                            value={emailPassword}
                            onChange={onEmailPasswordChange}
                            placeholder={emailPasswordConfigured ? t('setupWizard.step4.alreadySet') : t('setupWizard.step4.requiredToSave')}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>{t('setupWizard.step4.fromEmail')}</Label>
                        <TextInput
                            type="email"
                            value={emailData.fromEmail || ''}
                            onChange={(v) => onEmailChange({ ...emailData, fromEmail: v })}
                            placeholder="notifications@youracademy.com"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={onSaveEmail} disabled={saving || !canSaveEmail}
                        className="rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
                        {t('setupWizard.step4.saveEmail')}
                    </button>
                    <button type="button" onClick={onSkipEmail} disabled={saving || emailSaved}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
                        {t('setupWizard.step4.skipForNow')}
                    </button>
                </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h3 className="font-semibold text-slate-900">{t('setupWizard.step4.whatsappSection')}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{t('setupWizard.step4.whatsappSectionHint')}</p>
                    </div>
                    {whatsappSaved && <span className="text-xs font-semibold text-emerald-700">{t('setupWizard.saved')}</span>}
                    {whatsappSkipped && <span className="text-xs font-semibold text-slate-500">{t('setupWizard.skipped')}</span>}
                </div>

                {!waLicensed && (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        {t('setupWizard.step4.whatsappNotLicensed')}
                    </p>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <Label>{t('setupWizard.step4.watiBaseUrl')}</Label>
                        <TextInput
                            value={whatsappData.apiBaseUrl || ''}
                            onChange={(v) => onWhatsappChange({ ...whatsappData, apiBaseUrl: v })}
                            placeholder="https://live-server.wati.io"
                            disabled={!waLicensed}
                        />
                    </div>
                    <div>
                        <Label>{t('setupWizard.step4.channelNumber')}</Label>
                        <TextInput
                            value={whatsappData.channelNumber || ''}
                            onChange={(v) => onWhatsappChange({ ...whatsappData, channelNumber: v })}
                            disabled={!waLicensed}
                        />
                    </div>
                    <div>
                        <Label>{t('setupWizard.step4.watiToken')}</Label>
                        <TextInput
                            type="password"
                            value={whatsappToken}
                            onChange={onWhatsappTokenChange}
                            placeholder={whatsappData.apiToken === 'CONFIGURED' ? t('setupWizard.step4.alreadySet') : t('setupWizard.step4.pasteToken')}
                            disabled={!waLicensed}
                        />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={onSaveWhatsApp} disabled={saving || !waLicensed}
                        className="rounded-lg bg-primary-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50">
                        {t('setupWizard.step4.saveWhatsApp')}
                    </button>
                    <button type="button" onClick={onSkipWhatsApp} disabled={saving || whatsappSaved}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
                        {t('setupWizard.step4.skipForNow')}
                    </button>
                </div>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex gap-3">
                <button type="button" onClick={onBack} disabled={saving}
                    className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                    {t('setupWizard.back')}
                </button>
                <button type="button" onClick={onFinish} disabled={saving}
                    className="flex-[2] rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:opacity-60 transition-colors">
                    {saving ? t('setupWizard.finishing') : t('setupWizard.finishSetup')}
                </button>
            </div>
        </div>
    );
}

// ─── Step 5: Done ─────────────────────────────────────────────────────────────
function StepDone({ data, onEnter }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-6 text-center">
            <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100">
                    <svg className="h-8 w-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                    </svg>
                </div>
            </div>

            <div>
                <h2 className="text-xl font-bold text-slate-900">{t('setupWizard.step5.title')}</h2>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                    {t('setupWizard.step5.ready', { name: data.appName })}
                </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-left">
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">{t('setupWizard.step5.academyLabel')}</p>
                    <p className="text-sm font-semibold text-slate-800 truncate">{data.appName}</p>
                    {data.tagline && <p className="text-xs text-slate-500 truncate">{data.tagline}</p>}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">{t('setupWizard.step5.supportLabel')}</p>
                    <p className="text-sm font-semibold text-slate-800 truncate">{data.supportEmail}</p>
                    <p className="text-xs text-slate-500 truncate">{data.supportPhone}</p>
                </div>
            </div>

            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-left space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t('setupWizard.step5.communicationsLabel')}</p>
                <p className="text-sm text-slate-700">
                    {t('setupWizard.step5.outboundEmail')}{' '}
                    <span className={data.emailConfigured ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>
                        {data.emailConfigured ? t('setupWizard.configured') : t('setupWizard.notConfiguredEmail')}
                    </span>
                </p>
                <p className="text-sm text-slate-700">
                    {t('setupWizard.step5.whatsapp')}{' '}
                    <span className={data.whatsappConfigured ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>
                        {data.whatsappConfigured ? t('setupWizard.configured') : t('setupWizard.notConfiguredWhatsApp')}
                    </span>
                </p>
            </div>

            <button type="button" onClick={onEnter}
                className="w-full rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300">
                {t('setupWizard.enterDashboard')}
            </button>
        </div>
    );
}

// ─── Main wizard ──────────────────────────────────────────────────────────────
const TOTAL_STEPS = 5;
const WORKING_STEPS = 4;

const EMPTY_EMAIL = {
    enabled: true,
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: '',
    replyToEmail: '',
    useStarttls: true,
};

const EMPTY_WHATSAPP = {
    enabled: false,
    mockMode: false,
    apiBaseUrl: '',
    apiToken: '',
    channelNumber: '',
};

export default function SetupWizard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { refresh: refreshBranding } = useBranding();
    const { refresh: refreshRegional } = useRegionalSettings();

    const [step, setStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const [academyData, setAcademyData] = useState({
        appName: appConfig.appName !== 'ChessDesk' ? appConfig.appName : '',
        tagline: '',
        logoFile: null,
        logoPreview: null,
    });
    const [contactData, setContactData] = useState({ supportPhone: '', supportEmail: '' });
    const [regionPreset, setRegionPreset] = useState('india');
    const [enabledUiLanguages, setEnabledUiLanguages] = useState(['en']);
    const [defaultUiLanguage, setDefaultUiLanguage] = useState('en');
    const [emailData, setEmailData] = useState(EMPTY_EMAIL);
    const [emailPassword, setEmailPassword] = useState('');
    const [whatsappData, setWhatsappData] = useState(EMPTY_WHATSAPP);
    const [whatsappToken, setWhatsappToken] = useState('');
    const [emailSaved, setEmailSaved] = useState(false);
    const [whatsappSaved, setWhatsappSaved] = useState(false);
    const [emailSkipped, setEmailSkipped] = useState(false);
    const [whatsappSkipped, setWhatsappSkipped] = useState(false);
    const [doneSummary, setDoneSummary] = useState({ emailConfigured: false, whatsappConfigured: false });

    // Non-blocking: if setup is already done, redirect away — never gate the wizard UI on this.
    useEffect(() => {
        let cancelled = false;
        getSetupStatus()
            .then((data) => {
                if (!cancelled && data?.setupComplete) {
                    markSetupComplete();
                    navigate('/admin', { replace: true });
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    async function handleContactNext() {
        setSaving(true);
        setSaveError('');
        try {
            await API.post('/admin/branding/onboarding', null, {
                params: { appName: academyData.appName.trim(), tagline: academyData.tagline.trim() },
            });
            if (academyData.logoFile) {
                await uploadBrandingLogo(academyData.logoFile);
            }
            await API.post('/admin/branding/contact', null, {
                params: {
                    supportPhone: contactData.supportPhone.trim(),
                    supportEmail: contactData.supportEmail.trim(),
                },
            });
            await refreshBranding({ bustLogo: !!academyData.logoFile });
            setStep(2);
        } catch (err) {
            setSaveError(apiErrorMessage(err, t('setupWizard.saveError')));
        } finally {
            setSaving(false);
        }
    }

    async function loadCommunicationsDefaults() {
        // Keep SMTP fields blank during onboarding — never prefill from env leftovers or contact email.
        setEmailData(EMPTY_EMAIL);
        setEmailPassword('');
        try {
            const status = await getIntegrationStatus();
            if (status?.whatsapp) {
                setWhatsappData({ ...EMPTY_WHATSAPP, ...status.whatsapp });
            }
        } catch {
            // Non-blocking — admin can still enter values manually
        }
    }

    async function handleRegionalNext() {
        setSaving(true);
        setSaveError('');
        try {
            const regional = {
                ...(regionalPresetPayload(regionPreset) || regionalPresetPayload('india')),
                enabledUiLanguages: serializeEnabledUiLanguages(enabledUiLanguages),
                defaultUiLanguage: enabledUiLanguages.includes(defaultUiLanguage)
                    ? defaultUiLanguage
                    : 'en',
            };
            const saved = await saveOnboardingRegionalSettings(regional);
            if (!saved?.countryCode || !saved?.currencyCode) {
                throw new Error(t('setupWizard.regionalSaveError'));
            }
            await refreshRegional();
            await loadCommunicationsDefaults();
            setStep(3);
        } catch (err) {
            setSaveError(apiErrorMessage(err, t('setupWizard.saveError')));
        } finally {
            setSaving(false);
        }
    }

    async function handleSaveEmail() {
        setSaving(true);
        setSaveError('');
        try {
            const payload = {
                ...emailData,
                enabled: true,
                smtpPassword: emailPassword.trim() ? emailPassword.trim() : emailData.smtpPassword,
            };
            const saved = await saveEmailSettings(payload);
            setEmailData({ ...emailData, ...saved, smtpPassword: saved.smtpPassword || '' });
            setEmailPassword('');
            setEmailSaved(true);
            setEmailSkipped(false);
        } catch (err) {
            setSaveError(apiErrorMessage(err, t('setupWizard.emailSaveError')));
        } finally {
            setSaving(false);
        }
    }

    async function handleSaveWhatsApp() {
        setSaving(true);
        setSaveError('');
        try {
            const payload = {
                ...whatsappData,
                apiToken: whatsappToken.trim() ? whatsappToken.trim() : whatsappData.apiToken,
            };
            const saved = await saveWhatsAppIntegration(payload);
            setWhatsappData(saved);
            setWhatsappToken('');
            setWhatsappSaved(true);
            setWhatsappSkipped(false);
        } catch (err) {
            setSaveError(apiErrorMessage(err, t('setupWizard.whatsappSaveError')));
        } finally {
            setSaving(false);
        }
    }

    async function handleCommunicationsFinish() {
        setSaving(true);
        setSaveError('');
        try {
            const status = await getIntegrationStatus();
            setDoneSummary({
                emailConfigured: !!status?.email?.operational,
                whatsappConfigured: !!status?.whatsapp?.configured,
            });
            markSetupComplete();
            setStep(4);
        } catch (err) {
            setSaveError(apiErrorMessage(err, t('setupWizard.saveError')));
        } finally {
            setSaving(false);
        }
    }

    function enterApp() {
        navigate('/admin', { replace: true });
    }

    return (
        <div className="min-h-[calc(100vh-80px)] bg-gradient-to-br from-primary-50 via-white to-purple-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg">
                <div className="mb-8 text-center">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-600 shadow-lg mb-4">
                        <svg className="h-7 w-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12.75 11.25 15 15 9.75m6 2.25a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900">{t('setupWizard.welcomeTitle')}</h1>
                    <p className="mt-1 text-sm text-slate-500">{t('setupWizard.welcomeSubtitle')}</p>
                </div>

                <div className="rounded-2xl bg-white shadow-xl ring-1 ring-black/5 overflow-hidden">
                    <div className="h-1 bg-slate-100">
                        <div className="h-full bg-primary-600 transition-all duration-500"
                            style={{ width: `${((step + 1) / TOTAL_STEPS) * 100}%` }} />
                    </div>

                    <div className="p-7">
                        {step < WORKING_STEPS && (
                            <div className="flex items-center justify-between mb-6">
                                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">
                                    {t('setupWizard.stepOf', { current: step + 1, total: WORKING_STEPS })}
                                </span>
                                <StepDots current={step} total={WORKING_STEPS} />
                            </div>
                        )}

                        {step === 0 && (
                            <StepAcademy data={academyData} onChange={setAcademyData} onNext={() => setStep(1)} />
                        )}
                        {step === 1 && (
                            <StepContact
                                data={contactData}
                                onChange={setContactData}
                                onBack={() => setStep(0)}
                                onNext={handleContactNext}
                                saving={saving}
                                error={saveError}
                            />
                        )}
                        {step === 2 && (
                            <StepRegional
                                preset={regionPreset}
                                onPresetChange={setRegionPreset}
                                enabledUiLanguages={enabledUiLanguages}
                                defaultUiLanguage={defaultUiLanguage}
                                onEnabledUiLanguagesChange={setEnabledUiLanguages}
                                onDefaultUiLanguageChange={setDefaultUiLanguage}
                                onBack={() => setStep(1)}
                                onNext={handleRegionalNext}
                                saving={saving}
                                error={saveError}
                            />
                        )}
                        {step === 3 && (
                            <StepCommunications
                                supportEmail={contactData.supportEmail}
                                emailData={emailData}
                                onEmailChange={setEmailData}
                                emailPassword={emailPassword}
                                onEmailPasswordChange={setEmailPassword}
                                whatsappData={whatsappData}
                                onWhatsappChange={setWhatsappData}
                                whatsappToken={whatsappToken}
                                onWhatsappTokenChange={setWhatsappToken}
                                onBack={() => setStep(2)}
                                onSaveEmail={handleSaveEmail}
                                onSkipEmail={() => { setEmailSkipped(true); setEmailSaved(false); }}
                                onSaveWhatsApp={handleSaveWhatsApp}
                                onSkipWhatsApp={() => { setWhatsappSkipped(true); setWhatsappSaved(false); }}
                                onFinish={handleCommunicationsFinish}
                                saving={saving}
                                error={saveError}
                                emailSaved={emailSaved}
                                whatsappSaved={whatsappSaved}
                                emailSkipped={emailSkipped}
                                whatsappSkipped={whatsappSkipped}
                            />
                        )}
                        {step === 4 && (
                            <StepDone
                                data={{
                                    appName: academyData.appName,
                                    tagline: academyData.tagline,
                                    ...contactData,
                                    ...doneSummary,
                                }}
                                onEnter={enterApp}
                            />
                        )}
                    </div>
                </div>

                <p className="mt-6 text-center text-xs text-slate-400">
                    {t('setupWizard.footerHint')}
                </p>
            </div>
        </div>
    );
}
